// Formatage $ + seuils budgétaires pour le dashboard admin « Dépenses IA »
// (phase B du chantier tracking dépenses IA, cf. spec-tracking-tokens.md §4.4).
// Les seuils (D3 de la mini-spec, validés par Thierry) sont affichés ici en
// LECTURE SEULE : l'enforcement (alerte cron + kill-switch) est la phase C —
// ce module ne fait que positionner un montant par rapport aux seuils.

const CURRENCY_FMT = new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" });

/** Cents (entiers, `ai_runs.cost_cents`) → dollars formatés fr-CA (2 décimales). */
export function formatCents(costCents: number): string {
  return CURRENCY_FMT.format(costCents / 100);
}

// `timeZone: "UTC"` est nécessaire : la vue renvoie une valeur `date` pure
// ("2026-07-01", pas de composante horaire). `new Date("2026-07-01")` la
// parse en UTC minuit ; formater ensuite dans le fuseau local (Québec, UTC-4/-5)
// ferait glisser l'affichage au mois précédent (30 juin au lieu du 1er juillet).
const MONTH_FMT = new Intl.DateTimeFormat("fr-CA", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/** "2026-07-01" (colonne `month` de `llm_usage_monthly`) → "Juillet 2026". */
export function monthLabel(month: string): string {
  const label = MONTH_FMT.format(new Date(`${month}T00:00:00Z`));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// Seuils validés par Thierry (mini-spec §2.4/§4.5) : alerte à 50 $/mois,
// plafond « dur » à 150 $/mois. La phase C branchera un cron + kill-switch sur
// ces mêmes valeurs ; ici elles ne font que teinter l'affichage.
export const BUDGET_ALERT_CENTS = 5_000;
export const BUDGET_HARD_CENTS = 15_000;

export type BudgetTone = "neutral" | "warning" | "danger";

/** Ton sémantique (aligné `Badge`) selon la position du total vs les seuils. */
export function budgetTone(costCents: number): BudgetTone {
  if (costCents >= BUDGET_HARD_CENTS) return "danger";
  if (costCents >= BUDGET_ALERT_CENTS) return "warning";
  return "neutral";
}

/** Mention courte accolée au ton (Badge) — la couleur n'est jamais seule
 * porteuse de sens (§5.2 du DS) : le libellé rappelle le seuil concerné. */
export function budgetToneLabel(tone: BudgetTone): string {
  switch (tone) {
    case "danger":
      return `Plafond atteint (${formatCents(BUDGET_HARD_CENTS)})`;
    case "warning":
      return `Seuil d'alerte atteint (${formatCents(BUDGET_ALERT_CENTS)})`;
    default:
      return `Sous le seuil d'alerte (${formatCents(BUDGET_ALERT_CENTS)})`;
  }
}

// Libellés FR des valeurs de l'enum `ai_run_kind` (cf. migrations
// 20260511120000_init.sql, …_ai_runs_kind_audit.sql, …_kind_synthesize.sql,
// …_ai_runs_user_and_stt.sql). Un kind inconnu (futur ajout d'enum) retombe
// sur sa valeur brute plutôt que de planter l'affichage.
const KIND_LABEL: Record<string, string> = {
  transcribe: "Extraction",
  stt: "Transcription (STT)",
  stt_identify: "Identification locuteurs",
  audit: "Audit qualité",
  synthesize: "Synthèse",
  summarize: "Résumé",
  embed: "Embedding",
  cluster: "Regroupement",
};

export function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}
